import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Persistent banner shown on every page when the current user's profile
 * is incomplete. Per UX choice, we DO NOT lock them out — just nudge.
 */
export default function IncompleteProfileBanner() {
  const { api, user } = useAuth();
  const nav = useNavigate();
  const [pc, setPc] = useState(null);
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem('vos_incomplete_banner_dismissed') === '1'
  );

  useEffect(() => {
    if (!user) return;
    // Banner is an EMPLOYEE/freelancer/vendor concept (KYC + skills + bank).
    // Clients are not employees — never nag them here.
    const role = user.role || '';
    if (role === 'client_producer' || role === 'client_director') return;
    api.get('/hr/me/profile-completion').then(r => setPc(r.data)).catch(() => {});
  }, [api, user]);

  if (!user || !pc || pc.complete || dismissed) return null;
  // Defensive guard — even if pc somehow loaded, clients never see the banner.
  if (user.role === 'client_producer' || user.role === 'client_director') return null;

  const goFix = () => nav(`/hr/people/${user.user_id || user.id}`);

  return (
    <div className="bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent border-b border-amber-500/30 px-4 py-2 flex items-center gap-3" data-testid="incomplete-banner">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-amber-100">
          <span className="font-medium">Your profile is {pc.score}% complete.</span>{' '}
          <span className="text-amber-200/70">Missing: {pc.missing.join(', ')}.</span>
        </p>
      </div>
      <button onClick={goFix} data-testid="banner-fix-btn"
        className="text-[10px] px-2.5 py-1 rounded bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 font-medium">
        Complete now →
      </button>
      <button onClick={() => { setDismissed(true); sessionStorage.setItem('vos_incomplete_banner_dismissed', '1'); }}
        data-testid="banner-dismiss"
        className="text-amber-200/50 hover:text-amber-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
